/**
 * TODO:
 * - Password strength
 * - Recaptcha
 */
import cn from "classnames";
import { Form, Formik } from "formik";
import { Anchor } from "grommet";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useTitle } from "react-use";
import * as yup from "yup";
import signUpImage from "../../assets/images/sign-up.png";
import Button from "../../components/Button";
import Checkbox from "../../components/forms/Checkbox";
import { FormField } from "../../components/forms/shared";
import TextInput from "../../components/forms/TextInput";
import Logo from "../../components/Logo";
import { useMutation } from "../../helpers/forms";
import { ROUTE_APP, ROUTE_LOGIN } from "../../routes";
import { MutationState } from "../../store/modules/types";
import {
  createUserAsync,
  createUserClear
} from "../../store/modules/user/actions";
import { RootState } from "../../store/rootReducer";

const validationSchema = yup.object().shape({
  email: yup.string().required("Email is required.").email(),
  password: yup.string().required("Password is required.").min(8).max(30),
  firstName: yup.string().required("First name is required."),
  lastName: yup.string().required("Last name is required."),
  terms: yup.boolean().required().oneOf([true]),
});

const SignUp = () => {
  useTitle("Register - Due");
  const history = useHistory();
  const dispatch = useDispatch();
  const year = new Date().getFullYear();
  const create: MutationState<string> = useSelector(
    ({ user: { create } }: RootState) => create
  );
  const [error, setError] = useState("");

  useMutation({
    mutation: create,
    onError: (e) => {
      setError(e);
    },
    onSuccess: () => {
      history.push(ROUTE_APP);
    },
    clear: () => {
      dispatch(createUserClear());
    },
  });

  return (
    <div className="h-full flex bg-asparagus">
      <div
        className={cn(
          "flex flex-col overflow-auto w-full bg-white p-4",
          "lg:w-1/2 lg:items-center lg:justify-center lg:rounded-tr-2xl lg:rounded-br-2xl"
        )}
      >
        <Logo
          type="plain"
          className="block mb-6 lg:fixed lg:top-4 lg:left-6 w-24"
        />
        <div className="lg:p-2 w-full lg:w-8/12">
          <h1 className="text-3xl font-bold mb-6 text-gray-800">
            Let's get started.
          </h1>
          {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
          <Formik
            validateOnChange={false}
            validateOnBlur={true}
            validationSchema={validationSchema}
            initialValues={{
              email: "",
              password: "",
              firstName: "",
              lastName: "",
              terms: false,
            }}
            onSubmit={({ email, password, firstName, lastName }) => {
              dispatch(
                createUserAsync.request({
                  email,
                  password,
                  firstName,
                  lastName,
                })
              );
            }}
          >
            {({ values, errors, setFieldValue }) => (
              <Form>
                <div className="flex">
                  <div className="flex-1 pr-1">
                    <FormField
                      id="firstName"
                      label="First name"
                      error={errors.firstName}
                    >
                      <TextInput
                        id="firstName"
                        name="firstName"
                        onChange={(e) => {
                          setFieldValue("firstName", e.target.value);
                        }}
                      />
                    </FormField>
                  </div>
                  <div className="flex-1 pl-1">
                    <FormField
                      id="lastName"
                      label="Last name"
                      error={errors.lastName}
                    >
                      <TextInput
                        id="lastName"
                        name="lastName"
                        onChange={(e) => {
                          setFieldValue("lastName", e.target.value);
                        }}
                      />
                    </FormField>
                  </div>
                </div>
                <FormField id="email" label="Email" error={errors.email}>
                  <TextInput
                    id="email"
                    name="email"
                    onChange={(e) => {
                      setFieldValue("email", e.target.value);
                    }}
                  />
                </FormField>
                <FormField
                  id="password"
                  label="Password"
                  error={errors.password}
                >
                  <TextInput
                    id="password"
                    type="password"
                    name="password"
                    onChange={(e) => {
                      setFieldValue("password", e.target.value);
                    }}
                  />
                </FormField>
                <div className="my-6">
                  <label className="flex items-center">
                    <Checkbox
                      error={!!errors.terms}
                      checked={values.terms}
                      onChange={(e) => {
                        setFieldValue("terms", e.target.checked);
                      }}
                    />
                    <div className="ml-2 text-sm text-gray-600">
                      I've read and agree with{" "}
                      <Anchor href="">Terms of Service</Anchor> and our{" "}
                      <Anchor href="">Privacy Policy</Anchor>.
                    </div>
                  </label>
                  {errors.terms && (
                    <div className="text-red-500 text-xs mt-1">
                      You must agree to the terms to continue.
                    </div>
                  )}
                </div>

                <Button type="submit" size="lg" block disabled={create.loading}>
                  Create account
                </Button>

                <div className="text-sm text-gray-600 mt-6 text-center">
                  Already have an account?{" "}
                  <Link to={ROUTE_LOGIN} className="text-primary hover:no-underline">
                    Log In
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        <div className="lg:fixed pb-2 mt-6 text-center lg:pb-0 text-xs text-gray-500 bottom-2">
          Copyright © {year} Due Inc. All rights reserved.
        </div>
      </div>
      <div className="w-1/2 h-full items-center justify-center lg:flex hidden">
        <img className="w-6/12" src={signUpImage} alt="Sign Up" />
      </div>
      {/* <Footer /> */}
    </div>
  );
};

export default SignUp;
